import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	CustomDetailCategory,
	DataKYCFileTctWithFile,
	GetKYCInvestorInstanceRes,
	KYCFileType,
} from '@dealstack/shared/lib/generated';
import { getMIPKycFormsTemplates } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { getInvestorFormsForReview } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useParams, useSearchParams } from 'react-router-dom';
import {
	FormCategoryGroup,
	FormReviewCardData,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/types.ts';

interface Props {
	mipId?: string;
	currentInvestor?: GetKYCInvestorInstanceRes;
	enabled?: boolean;
}

const CATEGORY_ORDER: (CustomDetailCategory | null)[] = [
	CustomDetailCategory.TAX_RESIDENCY,
	CustomDetailCategory.FATCA,
	CustomDetailCategory.CRS,
	CustomDetailCategory.GDPR,
	null,
];

export const useFormsCategoryList = ({ mipId, currentInvestor, enabled = true }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { dealId } = useParams();
	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;

	const { data: templatesData, isLoading: isTemplatesLoading } = useQuery({
		queryKey: [API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_GET, mipId],
		queryFn: () => getMIPKycFormsTemplates({ archived: false }),
		enabled: enabled && !!mipId,
	});

	const { data: submissionsData, isLoading: isSubmissionsLoading } = useQuery({
		queryKey: [
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_INVESTOR_FORMS_GET,
			dealId,
			mipId,
			kycWorkflowId,
		],
		queryFn: () =>
			getInvestorFormsForReview({
				dealId: dealId ?? '',
				programmeId: mipId ?? '',
				kycWorkflowId: kycWorkflowId ?? '',
			}),
		enabled: enabled && !!mipId && !!dealId && !!kycWorkflowId,
	});

	const isLoading = isTemplatesLoading || isSubmissionsLoading;

	const titleForCategory = (category: CustomDetailCategory | null): string => {
		switch (category) {
			case CustomDetailCategory.TAX_RESIDENCY:
				return tMipManage('settings.tax.tax.label');
			case CustomDetailCategory.FATCA:
				return tMipManage('settings.tax.fatca.label');
			case CustomDetailCategory.CRS:
				return tMipManage('settings.tax.crs.label');
			case CustomDetailCategory.GDPR:
				return tMipManage('settings.tax.gdpr.label');
			default:
				return tMipManage('settings.tax.other.label');
		}
	};

	const groups = useMemo(() => {
		const templatesById = new Map(
			(templatesData?.payload ?? []).map((t) => [t.id, t] as const)
		);
		const submissions = submissionsData?.payload ?? [];

		const cards: FormReviewCardData[] = submissions.map((submission) => {
			const template = templatesById.get(submission.templateId);
			return {
				templateId: submission.templateId,
				templateName: submission.formName,
				templateFileId: submission.templateFileId,
				isSmartForm: template?.isPreFillable ?? false,
				status: submission.status,
				fileId: submission.fileId,
				fileName: submission.fileName,
				rejectionComment: submission.rejectionComment,
				subjectAction: submission.subjectAction,
				createdAt: submission.createdAt,
				instanceId: submission.instanceId,
				requiresSignature: submission.requiresSignature,
				signatureStatus: submission.signatureStatus,
			};
		});

		const categoryOf = (templateId: string): CustomDetailCategory | null =>
			templatesById.get(templateId)?.category ?? null;

		const groups: FormCategoryGroup[] = CATEGORY_ORDER.map((category) => ({
			key: category ?? 'OTHER',
			category,
			title: titleForCategory(category),
			cards: cards.filter((card) => categoryOf(card.templateId) === category),
		})).filter((group) => group.cards.length > 0);

		return groups;
	}, [templatesData?.payload, submissionsData?.payload, tMipManage, titleForCategory]);

	// Uncategorized (OTHER-type) KYC files are plain admin-uploaded docs with no form template — surfaced
	// in a dedicated "Uncategorized files" section under the forms.
	const uncategorizedFiles: DataKYCFileTctWithFile[] = useMemo(
		() =>
			(currentInvestor?.kycFiles ?? []).filter(
				(file) => file.type === KYCFileType.OTHER && !file.deleted
			),
		[currentInvestor?.kycFiles]
	);

	return {
		isLoading,
		groups,
		uncategorizedFiles,
	};
};
